import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Price from './Price';
import Purchase from './Purchase';
import ProductDetails from './ProductDetails';
import ProductImages from './ProductImages';
import viewAnalytics from './viewAnalytics';
import Top from '../Top/Top';
import Button from '../Elements/Button/Button';
import Radio from '../Elements/Radio/Radio';
// import Select from '../Elements/Select/Select';
import TemplateParser from '../Templates/TemplateParser';
import Products from '../Templates/Products/Products';
import LazyImage from '../../hocs/Lazy/LazyImage';
import Breadcrumbs from '../Category/Breadcrumbs';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Overlay from '../../hocs/Overlay/Overlay';
import { addRecentlyViewed } from '../../actions/checkout';
import { resetFilter } from '../../actions/environment';
import { difference } from '../../utils/array';
import { inchesToCentimeters } from '../../utils/conversion';
import { purifier, offset, scrollTo } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { hasTranslation, translate, prefix, translateObjectData } from '../../utils/locale';
import { urlify, urlifyAllowAccents, titleCase, camelCase, decode, cleanInternalLinks } from '../../utils/string';
import { origin } from '../../config';
import s from './Product.scss';

const Product = ({ location, data, currentPath, setup, productCache, filter, recentlyViewed, getParameters, customColor = {}, hashLocation, technicalDownloads = {}, pdpSwatches = [], ...props }) => {
	if (!data || !data.productData || !productCache?.[setup.market]?.[setup.pricelist]) return null;
	const product = translateObjectData(setup, data.productData);

	const { products: cache, filterArrays } = productCache[setup.market][setup.pricelist];
	const productId = product.id.slice(product.id.lastIndexOf('/') + 1);
	const productUri = urlifyAllowAccents(product.title);
	// console.log('DEBUG: Object.keys(cache)', Object.keys(cache).filter((k) => k.indexOf(productId) !== -1));
	const allVariants = product.variants.reduce((acc, { selectedOptions = {}, id }, i) => {
		let uri = Object.keys(selectedOptions).filter((type) => type !== 'size').reduce((a, type) => (`${a}_${urlifyAllowAccents(selectedOptions[type])}`), '');
		uri = uri.replace(/^_/, '');
		const cacheId = `${productId}${uri ? `_${uri}` : ''}`;

		if (cache[cacheId]) {
			uri = uri.replace(/_/g, '-') || cache[cacheId].uri;
			const size = cache[cacheId].sizes.length > 1 ? camelCase(Object.keys(selectedOptions).filter((type) => type === 'size').map((type) => selectedOptions[type]).join('')) : 'oneSize';
			if (acc[uri]) {
				acc[uri].variantKey[size] = i;
			} else {
				acc[uri] = {
					id: id?.[setup.market] || '',
					cacheId,
					selectedOptions,
					uri: cache[cacheId].uri,
					sizes: cache[cacheId].sizes,
					sizeUris: cache[cacheId].sizes.map((sz) => `${cache[cacheId].uri.replace(`${productUri}-`, '')}-${urlify(sz.name).replace(/-+/g, '-')}`),
					variantKey: {
						[size]: i,
					},
				};
			}
		}

		return acc;
	}, {});

	// console.log('DEBUG: allVariants', allVariants);

	let initialVariantKey = currentPath.slice(currentPath.lastIndexOf('/') + 1).replace(`${productUri}-`, '');
	if (getParameters.variant) {
		initialVariantKey = Object.keys(allVariants).filter((v) => allVariants[v].id.indexOf(getParameters.variant) !== -1)[0];
	}
	if (!allVariants[initialVariantKey]) {
		initialVariantKey = Object.keys(allVariants).filter((v) => allVariants[v].sizeUris.indexOf(initialVariantKey) !== -1)[0];
	}

	// console.log('DEBUG: initialVariantKey', initialVariantKey);
	const [currentVariantKey, updateVariant] = useState(initialVariantKey);
	const currentVariant = allVariants?.[currentVariantKey] || allVariants?.[Object.keys(allVariants)[0]];

	// Choose always the first size if not specified
	const matchingSize = currentVariant.sizes.filter((size) => location.pathname.indexOf(urlify(size.name).replace(/-+/g, '-')) !== -1);
	const initialProductPageSelection = {
		product: {
			[currentVariant.cacheId]: matchingSize?.[0]?.id || currentVariant.sizes[0].id,
		},
	};
	const initialSelectedSize = matchingSize?.[0]?.name || currentVariant.sizes[0].name;

	const [analytics, analyticsShown] = useState(false);
	const pageElement = useRef(null);
	// Selection ids before adding to cart. [[cacheId, selectionId], ...]
	const [productPageSelection, updateProductPageSelection] = useState(initialProductPageSelection);
	// Expand/collapse product choices.
	const [choiceSection, setChoiceSection] = useState('default');
	// Size selector
	const [selectedSize, selectSize] = useState(initialSelectedSize);
	// Add on selector seat pads
	const [selectedSeatPad, selectSeatPad] = useState('');
	// Add on selector glides
	const [selectedGlides, selectGlides] = useState('');
	// Showing custom color poup
	const [showingCustomColorPopup, toggleCustomColorPopup] = useState(false);

	useEffect(() => {
		if (analytics !== currentPath) {
			let updateVariantKey = currentPath.slice(currentPath.lastIndexOf('/') + 1).replace(`${productUri}-`, '');
			if (getParameters.variant) {
				updateVariantKey = Object.keys(allVariants).filter((v) => allVariants[v].id.indexOf(getParameters.variant) !== -1)[0];
			}
			if (!allVariants[updateVariantKey]) {
				updateVariantKey = Object.keys(allVariants).filter((v) => allVariants[v].sizeUris.indexOf(updateVariantKey) !== -1)[0];
			}
			if (updateVariantKey !== currentVariantKey) updateVariant(updateVariantKey);

			if (availableForPurchase) props.addRecentlyViewed(cachedProduct.variantId);
			let sku = product.variants.reduce((acc, size) => (!acc ? size.sku : acc), '');
			if (sku.indexOf('_') !== -1) sku = sku.slice(0, sku.indexOf('_'));
			viewAnalytics(price, product, setup, currentPath, sku);
			analyticsShown(currentPath);
		}
	}, [currentPath, product, cachedProduct, price, setup, analytics]);


	/* INTERNAL SCROLL */
	const scrollToHash = () => {
		let imagesLoaded = null;
		const localHashLocation = hashLocation || window.location.hash;

		const allLoaded = () => {
			if (pageElement && pageElement.current) {
				const imgs = pageElement.current.querySelectorAll('img');
				imagesLoaded = [...imgs].length;

				[...imgs].forEach((img) => {
					if (img.complete) imagesLoaded -= 1;
				});
			}

			if (pageElement && pageElement.current && imagesLoaded === 0) {
				// Delay to understand page navigation
				setTimeout(() => {
					const position = offset(document.getElementById(localHashLocation.replace('#', ''))).top - 80;
					scrollTo(500, position);
				}, 300);
			} else {
				setTimeout(allLoaded, 50);
			}
		};

		allLoaded();
	};

	useEffect(() => {
		if (hashLocation || window.location.hash !== '') scrollToHash();
	});

	if (!currentVariant) return true;

	const currentVariantKeySize = (selectedSize && currentVariant.variantKey[camelCase(selectedSize)]) || currentVariant.variantKey[Object.keys(currentVariant.variantKey)[0]];
	const variant = product.variants[currentVariantKeySize];

	const cachedProduct = translateObjectData(setup, cache[currentVariant.cacheId]);
	const availableForPurchase = Object.keys(cachedProduct).length > 0;
	const soldOut = variant.inventoryItem[setup.market] === 0;
	const price = cachedProduct.price[setup.currency];
	const discount = !!price.compareAtPrice || (100 - Math.round((price.price / price.compareAtPrice) * 100));
	const sizes = (cachedProduct.sizes && cachedProduct.sizes.map((item) => ({
		key: item.id,
		value: `${item.name}${item?.seatHeight ? ` (${item.seatHeight}" / ${inchesToCentimeters(item.seatHeight)}cm)` : ''}`,
		sku: item.sku,
		disabled: item.availability === 0,
		selected: selectedSize === item.name,
	}))) || [];

	const swatches = pdpSwatches
		.filter((swatch) => swatch.collection && swatch.collection.reduce((acc, sC) => [...acc, decode(sC.value)], []).indexOf(cachedProduct.collection) !== -1)
		.reduce((acc, swatch, i) => ({ ...acc, [urlify(swatch.title)]: { ...swatch, order: i } }), {});

	const shareLocation = `${origin}${prefix(setup, cachedProduct.canonicalUri)}`;
	const productSections = data.parts || [];

	// console.log('DEBUG: product, cachedProduct, variant', product, cachedProduct, variant);

	const metaInfo = {
		title: `${product.seo.title || product.title}`,
		metaDescription: product.seo.description || purifier(product.description, { ALLOWED_TAGS: ['#text'] }),
		linkCanonical: shareLocation,
	};

	const sortedImages = {
		general: [],
		skus: {},
	};
	const badges = [];
	let media = [];

	product.media.forEach((item) => {
		const imageAlt = `${urlifyAllowAccents(item.alt || '')}-${urlifyAllowAccents(item.src.slice(item.src.lastIndexOf('/') + 1)).replace(/[\s\-_]/g, '')}`.toLowerCase();
		sortedImages.general.push(item.src);
		Object.keys(allVariants).forEach((variantUri) => {
			const skus = allVariants[variantUri].sizes.map((size) => size.sku);
			skus.forEach((sku) => {
				// Check if SKU exists in "imageAlt"
				const reducedSku = sku.replace(/[\s\-_]/g, '').toLowerCase();
				if (imageAlt.indexOf(reducedSku) !== -1) {
					if (!sortedImages.skus[sku]) sortedImages.skus[sku] = [];
					sortedImages.skus[sku].push(item.src);
				}
			});
		});
	});

	if (Object.keys(sortedImages.skus).length > 0) {
		const sortedSkus = Object.keys(sortedImages.skus).sort((a, b) => {
			if (a.length > b.length) return -1;
			if (a.length < b.length) return 1;
			return a.localeCompare(b);
		});

		sortedSkus.forEach((indexSku, i) => {
			sortedSkus.slice(i + 1).forEach((sku) => {
				sortedImages.skus[sku] = difference(sortedImages.skus[sku], sortedImages.skus[indexSku]);
			});
		});
	}

	const generalImages = difference(sortedImages.general, Object.keys(sortedImages.skus).reduce((acc, sku) => [...acc, ...sortedImages.skus[sku]], []));

	const imagesBySku = cachedProduct.sizes
		// only selected size or if no sizes selected all sizes.
		.filter((size) => !selectedSize || (selectedSize && size.name === selectedSize))
		// get original image object based on sorted skus.
		.reduce((acc, size) => [...acc, ...product.media.filter((m) => sortedImages?.skus?.[size?.sku] && sortedImages.skus[size.sku].indexOf(m.src) !== -1)], []);

	media = [...imagesBySku, ...generalImages.reduce((acc, gI) => [...acc, ...product.media.filter((m) => m.src === gI)], [])];

	if (!media || media.length === 0) media = product.media;

	if (!soldOut && product?.tags && product.tags.indexOf('new') !== -1) {
		badges.unshift(<span key="new-badge" className={[s.productDetail_badge, s.productDetail_badge__new].join(' ')}>{ translate(setup, 'new') }</span>);
	} else if (!soldOut && !!price.compareAtPrice && price.compareAtPrice !== price.price) {
		badges.unshift(<span key="sale-badge" className={[s.productDetail_badge, s.productDetail_badge__sale].join(' ')}>-{ discount }%</span>);
	}

	const clickProductPreview = (e) => {
		e.preventDefault();
		const id = e.currentTarget.getAttribute('id');
		const link = e.currentTarget.getAttribute('href');
		updateVariant(id);

		if (selectedSize) {
			window.history.replaceState({}, '', prefix(setup, `${link}-${urlify(selectedSize)}`));

			const cachedPreviewSize = allVariants[id].sizes.filter((size) => selectedSize === size.name).map((size) => size.id).join('');
			updateProductPageSelection((prevState) => ({
				...prevState,
				product: {
					[allVariants[id].cacheId]: cachedPreviewSize,
				},
			}));
		} else {
			window.history.replaceState({}, '', prefix(setup, link));
		}
	};

	const choicesAccordion = (e) => {
		e.preventDefault();
		const $el = e.currentTarget;
		setChoiceSection((prevState) => {
			if (prevState === 'default' && [...$el.parentNode.parentNode.children].indexOf($el.parentNode) === 0) return '';
			if (prevState === $el.dataset.section) return '';
			return $el.dataset.section;
		});
	};

	const selectSizeHandler = (value) => {
		updateProductPageSelection((prevState) => ({
			...prevState,
			product: {
				[cachedProduct.variantId]: value,
			},
		}));
		const selectedSizeName = cachedProduct.sizes.filter((size) => size.id === value).map((size) => size.name).join('');

		window.history.replaceState({}, '', prefix(setup, `${location.pathname.slice(0, location.pathname.lastIndexOf('/'))}/${currentVariant.uri}-${urlify(selectedSizeName).replace(/-+/g, '-')}`));

		selectSize(selectedSizeName);
	};

	const selectSeatPadHandler = (e) => {
		e.preventDefault();
		const itemId = e.currentTarget.getAttribute('href').slice(1);
		selectSeatPad((prevState) => (prevState !== itemId ? itemId : ''));
	};

	const selectSeatPadIncludeHandler = (e) => {
		e.preventDefault();
		if (selectedSeatPad) {
			updateProductPageSelection((prevState) => ({
				...prevState,
				seatPad: {
					...prevState.seatPad,
					[selectedSeatPad]: cache[selectedSeatPad].sizes[0].id,
				},
			}));
			setChoiceSection('default');
		}
	};

	const selectGlidesHandler = (e) => {
		e.preventDefault();
		const itemId = e.currentTarget.getAttribute('href').slice(1);
		selectGlides((prevState) => (prevState !== itemId ? itemId : ''));
	};

	const selectGlidesIncludeHandler = (e) => {
		e.preventDefault();
		if (selectedGlides) {
			updateProductPageSelection((prevState) => ({
				...prevState,
				glides: {
					...prevState.glides,
					[selectedGlides]: cache[selectedGlides].sizes[0].id,
				},
			}));
			setChoiceSection('default');
		}
	};

	const removeSelectedItemHandler = (e) => {
		e.preventDefault();
		const itemId = e.currentTarget.getAttribute('href').replace('#remove-', '');
		const type = itemId.slice(0, itemId.indexOf('-'));
		updateProductPageSelection((prevState) => {
			// extract removed item and use rest to get remaining items.
			const { [itemId.replace(`${type}-`, '')]: removeThisItem, ...keepItems } = prevState[type];
			return {
				...prevState,
				[type]: {
					...keepItems,
				},
			};
		});
	};

	const toggleCustomColorPopupHandler = (e) => {
		e.preventDefault();
		toggleCustomColorPopup((prevState) => !prevState);
	};

	const closeCustomColorHandler = () => {
		toggleCustomColorPopup(false);
	};

	/* Extract variant information */

	const variants = Object.keys(allVariants).reduce((acc, item) => {
		// hasMatching must be equal to 1. Only one difference.
		const hasMatching = Object.keys(allVariants[item].selectedOptions).filter((iSO) => currentVariant.selectedOptions[iSO] && allVariants[item].selectedOptions[iSO] !== currentVariant.selectedOptions[iSO]);

		if (hasMatching.length === 1) {
			// const variantItem = product?.variants?.[allVariants[item].variantKey];
			const matchingKey = hasMatching[0];
			const matchingOption = allVariants[item].selectedOptions[matchingKey];
			const prevState = acc[matchingKey] || [{ swatch: swatches[urlify(currentVariant.selectedOptions[matchingKey])] || { order: 9e5 }, title: currentVariant.selectedOptions[matchingKey], uri: cachedProduct.uri, id: cachedProduct.variantId, selected: currentVariantKey === urlify(cachedProduct.variant) }];

			return {
				...acc,
				[matchingKey]: [
					...prevState,
					{ swatch: swatches[urlify(matchingOption)] || { order: 9e5 }, title: matchingOption, uri: allVariants[item].uri, id: item, selected: currentVariantKey === item },
				],
			};
		}

		return acc;
	}, {});


	/* Check for custom color variation */
	let hasCustomColor = [];
	if (customColor.assign) {
		hasCustomColor = customColor.assign.reduce((acc, section) => {
			if (section?.relatedProduct?.products && section.relatedProduct.products.indexOf(productId) !== -1) {
				acc = [...acc, urlify(section.variantCategory)];
			}
			return acc;
		}, []);
	}

	hasCustomColor.forEach((color) => {
		if (!variants[color]) variants[color] = [];
		variants[color].push({
			swatch: {
				color: 'conic-gradient(#F1BD47, #E79C3E, #DD7534, #D33E2F, #B35483, #715097, #3A4994, #71ACD0, #84BC99, #7CB34F, #D7DA51, #F3E252, #F1BD47)',
				order: 99e4,
			},
			title: translate(setup, 'customColor'),
			uri: `${productUri}-customColor`,
			id: `${productId}_customColor`,
			selected: showingCustomColorPopup,
		});
	});

	/* Extract add-ons */

	const addOnCategories = Object.keys(filterArrays.categories).filter((cat) => /^add-ons\//.test(cat) && filterArrays.categories[cat].filter((pId) => parseInt(pId, 10) === parseInt(productId, 10)).length > 0);
	const addOnGlides = addOnCategories.reduce((acc, cat) => {
		if (/^add-ons\/glides/.test(cat)) {
			return [...acc, ...filterArrays.categoryVariants[cat].filter((pVId) => cache?.[pVId]?.tags && cache[pVId].tags.indexOf('glides') !== -1 && pVId.indexOf('standard') === -1)];
		}
		return acc;
	}, []);
	/*
	const addOnProducts = addOnCategories.reduce((acc, cat) => {
		if (/^add-ons\/glides/.test(cat)) {
			return [...acc, ...filterArrays.categoryVariants[cat].filter((pVId) => cache?.[pVId]?.tags && cache[pVId].tags.indexOf('glides') === -1)];
		}
		return acc;
	}, []).reduce((acc, p) => ({ ...acc, [urlify(cache[p].name)]: { key: p, value: cache[p].name } }), {});
	*/
	const addOnSeatPads = addOnCategories.reduce((acc, cat) => {
		if (/^add-ons\/seat/.test(cat)) {
			return [...acc, ...filterArrays.categoryVariants[cat].filter((pVId) => cache?.[pVId]?.tags && cache[pVId].tags.indexOf('seat pad') !== -1)];
		}
		return acc;
	}, []);

	let seatPadTitle = translate(setup, 'addOnSeatPads');
	if (hasTranslation(setup, 'addOnSeatPadsTitle')) {
		const seatPadVariantTitles = setup.statics.addOnSeatPadsTitle;
		seatPadVariantTitles.forEach((version) => {
			const collectionMap = version.collection.reduce((acc, c) => [...acc, c.value], []);
			if (collectionMap.indexOf(product.productType) !== -1) {
				seatPadTitle = translate(setup, version.label);
			}
		});
	}

	return (
		<main role="main" ref={pageElement}>
			<Top setup={setup} pageContent={metaInfo} paths={data.paths} skipGeneral />
			<div id="productTop" className={s.product}>
				<Breadcrumbs currentPath={currentPath} filter={filter} filtering={productCache[setup.market][setup.pricelist]} data={data} setup={setup} product={cachedProduct.variant} resetFilter={props.resetFilter} />
				<ErrorBoundary message="Product images missing data" debugData={product}>
					<ProductImages media={media} currentVariantKey={currentVariantKey} />
				</ErrorBoundary>
				<div className={s.productDetail}>

					<ErrorBoundary message="Product details missing data" debugData={product}>
						<div className={s.productDetail_top}>
							<h1 className={s.productDetail_top_title}>{ `${product.title}${cachedProduct?.collection && cachedProduct.collection.indexOf('by') !== -1 ? ` ${cachedProduct.collection.slice(cachedProduct.collection.indexOf('by'))}` : ''}` }</h1>
							<span className={s.productDetail_top_sku}>{ `${translate(setup, 'sku')}: ${selectedSize ? sizes.filter((size) => size.selected).map((size) => size.sku).join('') : ''}` }</span>
							<span className={s.productDetail_top_price}>
								<Price setup={setup} price={variant.price} soldOut={soldOut} product />
							</span>
						</div>
						<div className={s.productDetail_purchase}>
							<ErrorBoundary message="Related Product details missing data" debugData={variants}>
								<div className={s.productDetail_variantSections}>
									{
										sizes.length > 1 && (
											<div className={[s.productDetail_variantSection, (choiceSection === 'default' || choiceSection === 'size' ? s.productDetail_variantSection__expanded : null)].join(' ')}>
												<button className={s.productDetail_variantSection_title} type="button" data-section="size" onClick={choicesAccordion}>
													<h3 className={s.productDetail_variantSection_title_text}>{ translate(setup, 'size') }</h3>
													<span className={s.productDetail_variantSection_title_selected}>{ selectedSize ? sizes.filter((size) => size.selected).map((size) => size.value).join('') : translate(setup, 'chooseSize') }</span>
													<svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
														<path d="M19 1L10 12.5845L1 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
													</svg>
												</button>
												<div className={s.productDetail_variantSection_variants}>
													<Radio variantClasses={['radio__size']} id="purchase" options={sizes} inputChange={selectSizeHandler} />
												</div>
											</div>
										)
									}
									{
										Object.keys(variants).sort((a, b) => a.localeCompare(b)).map((variantType, i) => (
											<div className={[s.productDetail_variantSection, ((choiceSection === 'default' && i === 0 && sizes.length <= 1) || choiceSection === variantType ? s.productDetail_variantSection__expanded : null)].join(' ')}>
												<button className={s.productDetail_variantSection_title} type="button" data-section={variantType} onClick={choicesAccordion}>
													<h3 className={s.productDetail_variantSection_title_text}>{ setup.statics.variantTypes.filter((vT) => urlify(vT.type) === urlify(variantType)).map((vT) => translate(setup, vT.label)).join('') || titleCase(variantType).replace('-', ' ') }</h3>
													<span className={s.productDetail_variantSection_title_selected}>{ variants[variantType].filter((item) => item.selected)?.[0]?.title }</span>
													<svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
														<path d="M19 1L10 12.5845L1 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
													</svg>
												</button>
												<div className={s.productDetail_variantSection_variants}>
													{
														variants[variantType].sort((a, b) => (a.swatch.order > b.swatch.order ? 1 : -1)).map((item) => {
															const VariantTag = item.selected ? 'span' : Link;
															const attrs = {};
															const itemClasses = [s.productDetail_variantSection_variant];
															if (!item.selected) {
																if (item.uri.indexOf('customColor') !== -1) {
																	attrs.to = prefix(setup, `${currentPath}#custom-color`);
																	attrs.onClick = toggleCustomColorPopupHandler;
																} else {
																	attrs.to = prefix(setup, `${currentPath.slice(0, currentPath.lastIndexOf('/'))}/${item.uri}`);
																	attrs.onClick = clickProductPreview;
																}
															} else {
																itemClasses.push(s.productDetail_variantSection_variant__selected);
															}
															if (item.uri.indexOf('customColor') !== -1) itemClasses.push(s.productDetail_variantSection_variant__customColor);
															if (!item.swatch.image && !item.swatch.color) itemClasses.push(s.productDetail_variantSection_variant__noSwatch);

															return (
																<VariantTag id={item.id} key={item.id} className={itemClasses.join(' ')} {...attrs}>
																	{
																		!item.swatch.image && !item.swatch.color && <span className={s.productDetail_variantSection_variant_text}>{item.title}</span>
																	}
																	{
																		!item.swatch.image && item.swatch.color && <span className={s.productDetail_variantSection_variant_swatch} style={{ background: item.swatch.color }} title={item.title} />
																	}
																	{
																		item.swatch.image && (
																			<span className={s.productDetail_variantSection_variant_swatch}>
																				<LazyImage {...imageSizeTranslation('1', item.swatch.image)} />
																			</span>
																		)
																	}
																	{
																		item.swatch.image && (
																			<span className={s.productDetail_variantSection_variant_hover}>
																				<span className={s.productDetail_variantSection_variant_hover_title}>{ item.title }</span>
																				<LazyImage {...imageSizeTranslation('1', item.swatch.image)} />
																			</span>
																		)
																	}
																</VariantTag>
															);
														})
													}
												</div>
											</div>
										))
									}
									{
										addOnSeatPads.length > 0 && product?.tags && product.tags.indexOf('seat pad') === -1 && (
											<div className={[s.productDetail_variantSection, (choiceSection === 'addOnSeatPads' ? s.productDetail_variantSection__expanded : null)].join(' ')}>
												<button className={s.productDetail_variantSection_title} type="button" data-section="addOnSeatPads" onClick={choicesAccordion}>
													<h3 className={s.productDetail_variantSection_title_text}>{ seatPadTitle }</h3>
													{ selectedSeatPad && cache[selectedSeatPad] && <span className={s.productDetail_variantSection_title_selected}>{ cache[selectedSeatPad].variant }</span> }
													<svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
														<path d="M19 1L10 12.5845L1 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
													</svg>
												</button>
												<div className={s.productDetail_variantSection_variants}>
													{
														addOnSeatPads.map((itemId) => {
															const item = cache[itemId];
															const addOnSwatch = pdpSwatches
																.filter((swatch) => (
																	swatch.collection
																	&& swatch.collection.reduce((acc, sC) => [...acc, sC.value], []).indexOf(item.collection) !== -1
																	&& urlify(swatch.title) === urlify(item.variant)
																))[0];
															const useAddOnSwatch = addOnSwatch || { image: item.media?.[0] };

															return (
																<a id={itemId} key={itemId} href={`#${itemId}`} onClick={selectSeatPadHandler} className={[s.productDetail_variantSection_variant, (selectedSeatPad === itemId ? s.productDetail_variantSection_variant__selected : null)].join(' ')}>
																	{
																		!useAddOnSwatch.image && useAddOnSwatch.color && <span className={s.productDetail_variantSection_variant_swatch} style={{ backgroundColor: useAddOnSwatch.color }} />
																	}
																	{
																		useAddOnSwatch.image && (
																			<span className={s.productDetail_variantSection_variant_swatch}>
																				<LazyImage {...imageSizeTranslation('1', useAddOnSwatch.image, addOnSwatch ? '-' : '_')} />
																			</span>
																		)
																	}
																	<span className={s.productDetail_variantSection_variant_hover}>
																		<span className={s.productDetail_variantSection_variant_hover_title}>{ item.variant }</span>
																		{ useAddOnSwatch.image && <LazyImage {...imageSizeTranslation('1', useAddOnSwatch.image, addOnSwatch ? '-' : '_')} /> }
																	</span>
																</a>
															);
														})
													}
													{
														selectedSeatPad && cache[selectedSeatPad] && (
															<div className={s.productDetail_variantSection_variants_selected}>
																<h3 className={s.productDetail_variantSection_variants_selected_title}>{ cache[selectedSeatPad].variant }</h3>
																<div dangerouslySetInnerHTML={{ __html: purifier(cache[selectedSeatPad].descriptionHtml, {}, setup) }} />
																<h3 className={s.productDetail_variantSection_variants_selected_price}>
																	<Price price={cache[selectedSeatPad].price} setup={setup} soldOut={!cache[selectedSeatPad].inStock ? translate(setup, 'soldOut') : false} />
																</h3>
																<div className={s.productDetail_variantSection_variants_selected_image}>
																	<LazyImage {...imageSizeTranslation('1', cache[selectedSeatPad].media[0], '_', false)} />
																</div>
																<Button variantClasses={['button__addBelow']} disabled={cache[selectedSeatPad].inStock === false} clickEventHandler={selectSeatPadIncludeHandler}>
																	{ translate(setup, 'addBelow') }
																</Button>
															</div>
														)
													}
												</div>
											</div>
										)
									}
									{
										addOnGlides.length > 0 && product?.tags && product.tags.indexOf('glides') === -1 && (
											<div className={[s.productDetail_variantSection, (choiceSection === 'addOnGlides' ? s.productDetail_variantSection__expanded : null)].join(' ')}>
												<button className={s.productDetail_variantSection_title} type="button" data-section="addOnGlides" onClick={choicesAccordion}>
													<h3 className={s.productDetail_variantSection_title_text}>{ translate(setup, 'addOnGlides') }</h3>
													{ selectedGlides && cache[selectedGlides] && <span className={s.productDetail_variantSection_title_selected}>{ cache[selectedGlides].name }</span> }
													<svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
														<path d="M19 1L10 12.5845L1 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
													</svg>
												</button>
												<div className={s.productDetail_variantSection_variants}>
													{
														addOnGlides.map((itemId) => {
															const item = cache[itemId];

															return (
																<a id={itemId} key={itemId} href={`#${itemId}`} onClick={selectGlidesHandler} className={[s.productDetail_variantSection_variant, s.productDetail_variantSection_variant__noSwatch, (selectedGlides === itemId ? s.productDetail_variantSection_variant__selected : null)].join(' ')}>
																	<span className={s.productDetail_variantSection_variant_text}>{ Object.keys(item.variations).map((v) => <span key={v}>{ item.variations[v] }</span>) }</span>
																</a>
															);
														})
													}
													{
														selectedGlides && cache[selectedGlides] && (
															<div className={s.productDetail_variantSection_variants_selected}>
																<h3 className={s.productDetail_variantSection_variants_selected_title}>{ cache[selectedGlides].name }</h3>
																<div dangerouslySetInnerHTML={{ __html: purifier(cache[selectedGlides].descriptionHtml, {}, setup) }} />
																<h3 className={s.productDetail_variantSection_variants_selected_price}><Price price={cache[selectedGlides].price} setup={setup} soldOut={!cache[selectedGlides].inStock ? translate(setup, 'soldOut') : false} /></h3>
																<div className={s.productDetail_variantSection_variants_selected_image}>
																	<LazyImage {...imageSizeTranslation('1', cache[selectedGlides].media[0], '_', false)} />
																</div>
																<Button variantClasses={['button__addBelow']} disabled={cache[selectedGlides].inStock === false} clickEventHandler={selectGlidesIncludeHandler}>
																	{ translate(setup, 'addBelow') }
																</Button>
															</div>
														)
													}
												</div>
											</div>
										)
									}
								</div>
							</ErrorBoundary>
						</div>
						<Purchase setup={setup} currentPath={currentPath} cache={cache} productPageSelection={productPageSelection} removeSelectedItemHandler={removeSelectedItemHandler} soldOut={soldOut ? translate(setup, 'soldOut') : false} variant={variant} />
						<ProductDetails setup={setup} product={product} variants={variants} variant={variant} cachedProduct={cachedProduct} technicalDownloads={technicalDownloads} />
						{
							hasCustomColor.length > 0 && customColor.popup && (
								<Overlay isShowing={showingCustomColorPopup} closeOverlayHandler={closeCustomColorHandler}>
									<div className={s.customColor}>
										{ customColor.popup.image && !customColor.popup.film && <LazyImage {...imageSizeTranslation('4', customColor.popup.image)} /> }
										{ customColor.popup.film && <video className={s.video_wrapper_src} autoPlay loop playsInline muted poster={customColor?.popup.popup?.image || null}><source src={customColor.popup.film.url} type="video/mp4" /></video> }
										<div className={s.customColor_description} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, customColor.popup.description)) }} />
										<div className={s.customColor_buttons}>
											{
												customColor.popup.links.map((l) => {
													const link = cleanInternalLinks(l.link.url);
													return <Button type="link" location={prefix(setup, link)}>{ l.link.title }</Button>;
												})
											}
										</div>
									</div>
								</Overlay>
							)
						}
					</ErrorBoundary>
				</div>
			</div>
			{
				productSections.length > 0 && (
					<div className={s.productCMS}>
						{
							productSections.map((section) => (
								<TemplateParser sections={section.sections} setup={setup} isProduct />
							))
						}
					</div>
				)
			}
			{
				productSections.length > 0 && (
					<ProductDetails setup={setup} product={product} variants={variants} variant={variant} cachedProduct={cachedProduct} technicalDownloads={technicalDownloads} bottom />
				)
			}
			<ErrorBoundary message="Product page filter error">
				<Products content={{ collection: [urlify(cachedProduct.collection)] }} />
			</ErrorBoundary>
		</main>
	);
};

const mapState = ({ location, environment, productCache, checkout, filter }, { data, currentPath }) => ({
	data,
	currentPath,
	hashLocation: environment.hashLocation,
	getParameters: environment.getParameters,
	pdpSwatches: environment.pdpSwatches,
	header: environment.header,
	setup: environment.setup,
	customColor: environment.customColor,
	technicalDownloads: environment.downloads.technicalDownloads || {},
	productCache,
	recentlyViewed: checkout.recentlyViewed,
	filter,
	location,
});

export default hot(module)(connect(mapState, { resetFilter, addRecentlyViewed })(Product));
